import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, Mail, MessageCircle, Phone, Trash2 } from 'lucide-react';
import { enquiriesAPI, ENQUIRIES_CHANGED_EVENT } from '../services/api';
import { Drawer, Popconfirm, Table, toast } from '../../../ui';
import { PageHeader, FilterBar, LoadingState, ErrorState, useDebouncedValue } from '../components';
import EmptyState from '../../../components/site/EmptyState';
import { formatDate, formatDateTime } from '../../../utils/format';
import { getErrorMessage } from '../../../utils/apiError';
import { phoneDigits } from '../utils/helpers';

// Route: /admin/enquiries — contact-form messages and custom-project requests from the site.

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', tone: 'info' },
  { value: 'in_progress', label: 'In progress', tone: 'warning' },
  { value: 'closed', label: 'Closed', tone: 'success' },
];
const STATUS = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

const TYPE_LABEL = { contact: 'Contact', project: 'Project request' };

const StatusBadge = ({ status }) => {
  const meta = STATUS[status] || { label: status || 'Unknown' };
  return (
    <span className="pk-badge" data-tone={meta.tone}>
      {meta.label}
    </span>
  );
};

const TypeBadge = ({ type }) => (
  <span className="pk-badge" data-tone={type === 'project' ? 'info' : undefined}>
    {TYPE_LABEL[type] || type || 'Other'}
  </span>
);

const firstLine = text =>
  String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .find(Boolean) || '';

const headline = enquiry => enquiry.subject?.trim() || firstLine(enquiry.message) || '(no message)';

// Our order references look like ORD-20261004-AB12CD34EF; anything id-shaped can be opened in the admin.
const looksLikeOrderId = value => /^[A-Za-z0-9][A-Za-z0-9_-]{5,63}$/.test(String(value || '').trim());

// wa.me needs the full international number; a bare 10-digit number is taken as Indian (+91).
const whatsappNumber = phone => {
  const digits = phoneDigits(phone);
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith('0')) return `91${digits.slice(1)}`;
  return digits.length >= 11 ? digits : '';
};

const notifyChanged = () => {
  try {
    window.dispatchEvent(new Event(ENQUIRIES_CHANGED_EVENT));
  } catch {
    // Non-browser environment.
  }
};

/** Everything about one enquiry, plus the status/note form and delete. */
const EnquiryDetails = ({ enquiry, onUpdated, onDeleted }) => {
  const [status, setStatus] = useState(enquiry.status || 'new');
  const [note, setNote] = useState(enquiry.adminNote || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(enquiry.status || 'new');
    setNote(enquiry.adminNote || '');
  }, [enquiry.id, enquiry.status, enquiry.adminNote]);

  const dirty = status !== (enquiry.status || 'new') || note.trim() !== (enquiry.adminNote || '').trim();

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await enquiriesAPI.updateStatus(enquiry.id, status, note.trim() || null);
      const updated = response.data?.data || { ...enquiry, status, adminNote: note.trim(), updatedAt: new Date().toISOString() };
      toast.success('Enquiry updated');
      onUpdated(updated);
      notifyChanged();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't update the enquiry."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await enquiriesAPI.delete(enquiry.id);
      toast.success('Enquiry deleted');
      onDeleted(enquiry);
      notifyChanged();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't delete the enquiry."));
    }
  };

  const whatsapp = whatsappNumber(enquiry.phone);
  const telDigits = phoneDigits(enquiry.phone);
  const hasProjectFields = enquiry.projectType || enquiry.budget || enquiry.timeline;
  const orderId = String(enquiry.orderId || '').trim();

  return (
    <>
      <div className="adm-detail">
        <div className="pk-cluster" style={{ gap: 8 }}>
          <TypeBadge type={enquiry.type} />
          <StatusBadge status={enquiry.status} />
          <span className="adm-note">Received {formatDateTime(enquiry.createdAt)}</span>
        </div>
      </div>

      <div className="adm-detail">
        <h3 className="adm-detail__label">{enquiry.subject?.trim() || 'Message'}</h3>
        <p className="adm-message">{enquiry.message || '(no message)'}</p>
      </div>

      <div className="adm-detail">
        <h3 className="adm-detail__label">From</h3>
        <dl className="pk-rows">
          <div>
            <dt>Name</dt>
            <dd>{enquiry.name || '—'}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>
              {enquiry.email ? (
                <a className="pk-link" href={`mailto:${enquiry.email}`}>
                  {enquiry.email}
                </a>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{enquiry.phone || '—'}</dd>
          </div>
        </dl>
        {(enquiry.email || telDigits || whatsapp) && (
          <div className="pk-cluster" style={{ gap: 8, marginTop: 12 }}>
            {enquiry.email && (
              <a className="pk-btn pk-btn--secondary pk-btn--xs" href={`mailto:${enquiry.email}?subject=${encodeURIComponent(`Re: ${headline(enquiry)}`)}`}>
                <Mail aria-hidden="true" />
                Email
              </a>
            )}
            {telDigits && (
              <a className="pk-btn pk-btn--secondary pk-btn--xs" href={`tel:${String(enquiry.phone).trim().startsWith('+') ? '+' : ''}${telDigits}`}>
                <Phone aria-hidden="true" />
                Call
              </a>
            )}
            {whatsapp && (
              <a className="pk-btn pk-btn--secondary pk-btn--xs" href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                <MessageCircle aria-hidden="true" />
                WhatsApp
              </a>
            )}
          </div>
        )}
      </div>

      {(hasProjectFields || orderId) && (
        <div className="adm-detail">
          <h3 className="adm-detail__label">{hasProjectFields ? 'Project' : 'Order'}</h3>
          <dl className="pk-rows">
            {enquiry.projectType && (
              <div>
                <dt>Project type</dt>
                <dd>{enquiry.projectType}</dd>
              </div>
            )}
            {enquiry.budget && (
              <div>
                <dt>Budget</dt>
                <dd>{enquiry.budget}</dd>
              </div>
            )}
            {enquiry.timeline && (
              <div>
                <dt>Timeline</dt>
                <dd>{enquiry.timeline}</dd>
              </div>
            )}
            {orderId && (
              <div>
                <dt>Order</dt>
                <dd>
                  {looksLikeOrderId(orderId) ? (
                    <Link className="pk-link" to={`/admin/orders/${encodeURIComponent(orderId)}`}>
                      {orderId}
                    </Link>
                  ) : (
                    orderId
                  )}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}

      <form className="adm-detail" onSubmit={save}>
        <h3 className="adm-detail__label">Follow-up</h3>
        <div className="form-item">
          <label className="form-label" htmlFor={`enquiry-status-${enquiry.id}`}>
            Status
          </label>
          <select
            id={`enquiry-status-${enquiry.id}`}
            className="pk-select"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-item">
          <label className="form-label" htmlFor={`enquiry-note-${enquiry.id}`}>
            Internal note <span className="pk-optional">(only admins see this)</span>
          </label>
          <textarea
            id={`enquiry-note-${enquiry.id}`}
            className="pk-textarea"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="e.g. Called on Monday, sending a quote"
          />
        </div>
        <div className="pk-cluster" style={{ justifyContent: 'space-between' }}>
          <Popconfirm title="Delete this enquiry?" description="This can't be undone." okText="Delete" okDanger onConfirm={remove}>
            <button type="button" className="pk-btn pk-btn--danger pk-btn--xs">
              <Trash2 aria-hidden="true" />
              Delete
            </button>
          </Popconfirm>
          <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={!dirty || saving} aria-busy={saving || undefined}>
            Save
          </button>
        </div>
        {enquiry.updatedAt && enquiry.updatedAt !== enquiry.createdAt && (
          <p className="adm-note" style={{ marginTop: 12, marginBottom: 0 }}>
            Last updated {formatDateTime(enquiry.updatedAt)}
          </p>
        )}
      </form>
    </>
  );
};

const EnquiryManagement = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 400);

  const [openId, setOpenId] = useState(null);
  const latestRequest = useRef(0);

  const hasFilters = Boolean(statusFilter || typeFilter || debouncedSearch);

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);
    try {
      const response = await enquiriesAPI.getAll({
        status: statusFilter,
        type: typeFilter,
        searchTerm: debouncedSearch,
        currentPage: page,
        itemsPerPage: PAGE_SIZE,
      });
      if (requestId !== latestRequest.current) return;
      const data = response.data?.data || {};
      const list = Array.isArray(data.items) ? data.items : [];
      setItems(list);
      setTotal(Number(data.totalCount) || 0);
      if (list.length === 0 && page > 1 && Number(data.totalCount) > 0) setPage(p => Math.max(1, p - 1));
    } catch (err) {
      if (requestId === latestRequest.current) setError(getErrorMessage(err, "Couldn't load enquiries."));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [statusFilter, typeFilter, debouncedSearch, page]);

  useEffect(() => {
    load();
  }, [load]);

  // New filters start from the first page.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, debouncedSearch]);

  const open = useMemo(() => items.find(item => item.id === openId) || null, [items, openId]);

  const handleUpdated = updated => {
    setItems(list => list.map(item => (item.id === updated.id ? { ...item, ...updated } : item)));
  };

  const handleDeleted = enquiry => {
    setOpenId(null);
    setItems(list => list.filter(item => item.id !== enquiry.id));
    setTotal(t => Math.max(0, t - 1));
    load();
  };

  const columns = [
    {
      title: 'Received',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: date => (
        <span className="adm-nowrap" title={formatDateTime(date)}>
          {formatDate(date)}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: type => <TypeBadge type={type} />,
    },
    {
      title: 'From',
      key: 'from',
      width: 220,
      render: (_, record) => (
        <div className="adm-cell__text">
          <div className="adm-cell-title">{record.name || 'No name'}</div>
          <div className="adm-cell-sub">{record.email || record.phone || ''}</div>
        </div>
      ),
    },
    {
      title: 'Subject',
      key: 'subject',
      render: (_, record) => (
        <button
          type="button"
          className="pk-link"
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', fontWeight: record.status === 'new' ? 600 : 400 }}
          onClick={event => {
            event.stopPropagation();
            setOpenId(record.id);
          }}
        >
          <span className="adm-clamp-2">{headline(record)}</span>
        </button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: status => <StatusBadge status={status} />,
    },
  ];

  let body;
  if (loading && items.length === 0 && !error) {
    body = <LoadingState label="Loading enquiries" />;
  } else if (error) {
    body = (
      <div className="adm-panel__state">
        <ErrorState title="Couldn't load enquiries" message={error} onRetry={load} />
      </div>
    );
  } else if (!hasFilters && total === 0) {
    body = (
      <div className="adm-panel__state">
        <EmptyState
          icon={<Inbox />}
          title="No enquiries yet"
          text="Messages from the contact form and custom-project requests will appear here."
        />
      </div>
    );
  } else {
    body = (
      <Table
        dataSource={items}
        columns={columns}
        loading={loading}
        rowKey="id"
        scroll={{ x: 820 }}
        emptyText="No enquiries match these filters."
        rowClassName="adm-row-link"
        onRow={record => ({ onClick: () => setOpenId(record.id) })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showTotal: (count, range) => `${range[0]}-${range[1]} of ${count}`,
          onChange: nextPage => setPage(nextPage),
        }}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Enquiries"
        description="Contact-form messages and custom-project requests. Open one to reply, add a note or change its status."
      />

      <FilterBar
        searchLabel="Search enquiries"
        searchPlaceholder="Search name, email, phone or message"
        searchValue={search}
        onSearchChange={setSearch}
        filters={[
          {
            key: 'status',
            component: (
              <select className="pk-select" aria-label="Filter by status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">Any status</option>
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            key: 'type',
            component: (
              <select className="pk-select" aria-label="Filter by type" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                <option value="contact">Contact</option>
                <option value="project">Project requests</option>
              </select>
            ),
          },
        ]}
        onClearFilters={
          search || statusFilter || typeFilter
            ? () => {
                setSearch('');
                setStatusFilter('');
                setTypeFilter('');
              }
            : undefined
        }
        end={!error && <span className="adm-toolbar__count">{total} total</span>}
      />

      <section className="adm-panel" aria-label="Enquiries">
        {body}
      </section>

      <Drawer
        open={Boolean(open)}
        onClose={() => setOpenId(null)}
        title={open ? open.name || 'Enquiry' : 'Enquiry'}
        width={520}
        className="adm-drawer"
      >
        {open && <EnquiryDetails enquiry={open} onUpdated={handleUpdated} onDeleted={handleDeleted} />}
      </Drawer>
    </div>
  );
};

export default EnquiryManagement;
